import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Col, Divider, Row } from 'antd';
import { cloneDeep, maxBy } from 'es-toolkit';

import { convertToFloat, routeFileSelector } from '@/utilz';
import EmptyRecords from '@/views/components/Common/EmptyRecords';
import ScrollPagination from '@/views/components/Paginations/ScrollPagination';
import ContentSpinner from '@/views/components/Spinners/ContentSpinner';
import FooterContentCard from '@/views/screens/common/FooterContentCard';

import { LADCovertDatesFromMomentToAPIType } from '../../../../../../utilz/PreClaimsUtils';
import Button from '../../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import { useGetClaimDetails, useGetClaimSettlements, useSaveClaim } from '../../hooks';
import { getTabDisplayState } from '../utils/getTabDisplayState';
import LADSettlementCard from './LADSettlementCard';

const LADSettlementsTab = ({ claimKey }) => {
  const { data: claimDetails } = useGetClaimDetails(claimKey);
  const {
    data: claimSettlementsData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useGetClaimSettlements({ claimKey });
  const { mutate: saveClaim } = useSaveClaim();
  const [newSettlements, setNewSettlements] = useState([]);
  const [settlementEditModes, setSettlementEditModes] = useState({});
  const nextLocalSettlementKeyRef = useRef(1);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const claimSettlements = claimSettlementsData?.data ?? [];
  const claimSettlementsTotalAmount = claimSettlementsData?.totalAmount ?? 0;
  const settlementList = useMemo(() => {
    const uniqueExistingSettlements = Array.from(
      new Map((claimSettlements || []).map((settlement) => [settlement.settlementSeq, settlement])).values(),
    );
    const existingSettlements = uniqueExistingSettlements.map((settlement) => ({
      ...settlement,
      amount: settlement.amount != null ? convertToFloat(settlement.amount) : settlement.amount,
      editMode: settlementEditModes[settlement.settlementSeq] ?? settlement.editMode ?? false,
      isNew: false,
    }));
    return [...newSettlements, ...existingSettlements];
  }, [claimSettlements, newSettlements, settlementEditModes]);
  const { showContent, showEmpty, showFullLoader, showNextPageLoader } = useMemo(
    () =>
      getTabDisplayState({
        hasRows: settlementList.length > 0,
        isFetching,
        isFetchingNextPage,
        isLoading,
      }),
    [isFetching, isFetchingNextPage, isLoading, settlementList.length],
  );

  const getSettlementKey = (settlement) =>
    settlement?.isNew && settlement?.localSettlementKey ? settlement.localSettlementKey : settlement?.settlementSeq;

  const setEditMode = (index, val) => {
    const settlement = settlementList[index];
    if (!settlement) {
      return;
    }
    if (settlement.isNew) {
      setNewSettlements((prev) =>
        prev.map((row) => (getSettlementKey(row) === getSettlementKey(settlement) ? { ...row, editMode: val } : row)),
      );
      return;
    }
    setSettlementEditModes((prev) => ({ ...prev, [getSettlementKey(settlement)]: val }));
  };

  const handleCancel = (index, isNew) => {
    const currentSettlement = settlementList[index];
    if (!currentSettlement) {
      return;
    }
    if (isNew) {
      setNewSettlements((prev) => prev.filter((row) => getSettlementKey(row) !== getSettlementKey(currentSettlement)));
      return;
    }
    const originalSettlement = (claimSettlements || []).find(
      (s) => s.settlementSeq === currentSettlement.settlementSeq,
    );
    if (originalSettlement) {
      setSettlementEditModes((prev) => ({ ...prev, [getSettlementKey(originalSettlement)]: false }));
    } else {
      setSettlementEditModes((prev) => ({ ...prev, [getSettlementKey(currentSettlement)]: false }));
    }
  };

  const handleSubmit = async (data, index) => {
    const handleSuccess = () => {
      setEditMode(index, false);
    };

    if (!claimDetails) {
      return;
    }
    const claimDetailsLocal = cloneDeep(claimDetails);

    const submittedSettlement = settlementList[index];
    if (!submittedSettlement) {
      return;
    }

    if (submittedSettlement.isNew) {
      setNewSettlements((prev) =>
        prev.map((row) =>
          getSettlementKey(row) === getSettlementKey(submittedSettlement)
            ? { ...row, ...data, editMode: false, isNew: true }
            : row,
        ),
      );
    }

    claimDetailsLocal.claimSettlement = [{ ...data, dirtyFlag: submittedSettlement.isNew ? 'I' : 'U' }];
    const formData = LADCovertDatesFromMomentToAPIType(claimDetailsLocal);
    if (handleSuccess) {
      formData.handleSuccess = handleSuccess;
    }
    formData.section = 'SETTLEMENTS';
    formData.pageIndex = claimSettlementsData?.pageIndex ?? 1;
    saveClaim(formData, {
      onSuccess: () => {
        handleSuccess();
        if (submittedSettlement.isNew) {
          setNewSettlements((prev) =>
            prev.filter((row) => getSettlementKey(row) !== getSettlementKey(submittedSettlement)),
          );
        }
      },
    });
  };

  const handleDelete = (index) => {
    const settlementToDelete = settlementList[index];
    if (!settlementToDelete) {
      return;
    }
    if (settlementToDelete.isNew) {
      setNewSettlements((prev) => prev.filter((row) => getSettlementKey(row) !== getSettlementKey(settlementToDelete)));
      return;
    }
    if (!claimDetails) {
      return;
    }
    const claimDetailsLocal = cloneDeep(claimDetails);

    setSettlementEditModes((prev) => ({ ...prev, [getSettlementKey(settlementToDelete)]: false }));
    claimDetailsLocal.claimSettlement = [{ ...settlementToDelete, dirtyFlag: 'D' }];
    const formData = LADCovertDatesFromMomentToAPIType(claimDetailsLocal);

    saveClaim({
      ...formData,
      pageIndex: claimSettlementsData?.pageIndex ?? 1,
      section: 'SETTLEMENTS',
    });
  };

  const handleAddNew = () => {
    const allSettlements = [...(claimSettlements || []), ...newSettlements];
    const maxSettlement = maxBy(allSettlements, ({ settlementSeq }) => Number(settlementSeq));
    const nextSeq = (Number(maxSettlement?.settlementSeq) || 0) + 1;

    setNewSettlements((prev) => [
      {
        accountingCode: undefined,
        amount: undefined,
        claimKey: claimDetails?.claimKey,
        dirtyFlag: 'i',
        editMode: true,
        isNew: true,
        localSettlementKey: `new-settlement-${nextLocalSettlementKeyRef.current++}`,
        settlementSeq: nextSeq,
      },
      ...prev,
    ]);
  };

  const handleNextPage = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <div className="d-flex py-16 h-100" style={{ flexDirection: 'column' }}>
      <Row className="px-30 align-items-center" justify="space-between">
        <Col>
          <PrimaryLabel className="fw-700 f-18">Claim Settlements</PrimaryLabel>
        </Col>
        <Col>
          <Row justify="end" gutter={16}>
            <Col>
              <Button onClick={handleAddNew}>Add New Settlement</Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider className="mt-0 my-16 ml-n16 vw-100" />
      {showFullLoader ? (
        <div className="px-30 py-10 flex-1" data-testid="lad-settlements-loading">
          <ContentSpinner />
        </div>
      ) : showContent ? (
        <ScrollPagination
          className="px-30 py-10 flex-1"
          loadMore={handleNextPage}
          isLoading={showNextPageLoader}
          hasMore={isFetchingNextPage ? false : (hasNextPage ?? false)}
          scrollOffset={30}
        >
          {settlementList.map((row, index) => (
            <LADSettlementCard
              settlementDetails={row}
              key={getSettlementKey(row)}
              index={index}
              editMode={Boolean(row.editMode)}
              setEditMode={setEditMode}
              handleCancel={handleCancel}
              handleSubmit={handleSubmit}
              handleDelete={handleDelete}
            />
          ))}
        </ScrollPagination>
      ) : showEmpty ? (
        <div className="px-30 py-10 flex-1">
          <EmptyRecords
            title="No Records Found"
            descriptionOne="No Settlement amount to this claim. Select 'Add New Settlement' to add Settlement."
            descriptionTwo=""
          />
        </div>
      ) : null}
      <Row justify="start" className="footer-container background-header mx-30 py-10">
        <Col md={12} />
        <Col md={6} className="px-15">
          <FooterContentCard
            justify="start"
            count={Number(claimSettlementsTotalAmount)?.toFixed(2)}
            label={t('translation:claims.totalAmount')}
          />
        </Col>
      </Row>
    </div>
  );
};

export default LADSettlementsTab;
